import { createRouter, createWebHistory } from 'vue-router';
import { getToken } from '@/api/client';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', name: 'home', component: () => import('@/views/HomeView.vue'), meta: { title: '创作工作台' } },
    { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { title: '登录', public: true, bare: true } },
    /**
     * ★ 对外产品展示（独立页面）
     *
     * 目标要求「对外产品展示**单独组织**；合成案例不得包装为真实客户成果」。
     * 所以它是一条独立的公开路由，而不是在工作区里加一块：
     *   · `public` —— 没登录也能看（对外）；
     *   · `bare`   —— 不套 AppShell（不带客户工作区的侧栏/底栏，避免被当成功能区）；
     *   · 页面内**不调用任何接口**，只有合成示例 + 显眼的免责声明。
     * 这样"展示"与"客户真实数据"在物理上就分开了。
     */
    { path: '/showcase', name: 'showcase', component: () => import('@/views/ShowcaseView.vue'), meta: { title: '产品能力展示', public: true, bare: true } },
    /**
     * ★ 独立工具入口
     *
     * 工具要能**单独进、单独跑**，不必须从智能体对话里绕一圈。
     * `/skills` 是老路径（可能有人收藏/分享过），保留为主路径；
     * `/tools` 是别名 —— 亮哥嘴里说的、文档里写的都是「工具」。
     * 用 alias 而不是 redirect：redirect 会把 `?tool=xxx` 深链参数丢掉。
     */
    {
      path: '/skills',
      alias: '/tools',
      name: 'skills',
      component: () => import('@/views/SkillsView.vue'),
      meta: { title: '工具' },
    },
    { path: '/business', name: 'business', component: () => import('@/views/BusinessView.vue'), meta: { title: '生意档案' } },
    /**
     * ★ 账号经营（P1 新增，2026-10-04 重构）
     *
     * 主导航收敛后的第一个业务入口：经营账号 / 策略版本 / 内容栏目 / 周计划。
     * 与「生意档案」(/business) 的分工：
     *   · 经营账号 = 面向**哪类客户**做哪个号（可以有多个）
     *   · 生意档案 = 这个号**卖什么**（描述商品/服务本身）
     * 两者都保留，`/business` 的旧链接照旧可用（本轮只改分组、不改路径）。
     */
    { path: '/operation', name: 'operation', component: () => import('@/views/OperationView.vue'), meta: { title: '账号经营' } },
    { path: '/benchmark', name: 'benchmark', component: () => import('@/views/BenchmarkView.vue'), meta: { title: '对标分析' } },
    /**
     * 爆款解析复刻：**独立入口**（工具页那张卡片的「打开」也来这儿）。
     * 必须自己一条路径 —— 它要用户先贴一段爆款文案，和通用工具确认框不是一回事。
     */
    { path: '/remix', name: 'remix', component: () => import('@/views/RemixView.vue'), meta: { title: '爆款解析复刻' } },
    { path: '/topics', name: 'topics', component: () => import('@/views/TopicsView.vue'), meta: { title: '选题与脚本' } },
    { path: '/tasks', name: 'tasks', component: () => import('@/views/TasksView.vue'), meta: { title: '生成中心' } },
    /**
     * ★ 内容任务（P2 首条链，2026-10-05）
     *
     * 「周计划条目 → 内容任务 → 文字脚本 → 版本化审批 → 预算 → 媒体暂停」这条链的**真实页面**：
     *   · `/content-tasks`     任务列表（真实 `GET /content-tasks`）
     *   · `/content-tasks/:id` 任务详情（`:id` = `ContentTask.publicId`，**对外唯一任务身份**）
     * 与交互稿 `/review/p2-chain`（`public` + `bare`，不接接口）是**两回事**：那屏是评审用的稿子，
     * 这两条是真的接后端、真的能点。用不同的路径段，避免"评审稿"和"功能"互相顶掉。
     */
    { path: '/content-tasks', name: 'content-tasks', component: () => import('@/views/ContentTasksView.vue'), meta: { title: '内容任务' } },
    {
      path: '/content-tasks/:id',
      name: 'content-task-detail',
      component: () => import('@/views/ContentTaskDetailView.vue'),
      meta: { title: '内容任务详情' },
    },
    { path: '/works', name: 'works', component: () => import('@/views/WorksView.vue'), meta: { title: '作品库' } },
    /**
     * 交付成果（文字交付中心）
     *
     * 之前文字类成果只存在于接口里：智能体回一句"生成营销方案《…》，6 个小节"，
     * 正文没有任何页面能读。用户花了积分拿到的东西读不了，等于没交付。
     */
    { path: '/deliverables', name: 'deliverables', component: () => import('@/views/DeliverablesView.vue'), meta: { title: '交付成果' } },
    /**
     * 分发中心
     *
     * 一键分发的能力原来只挂在作品库（一次看一条片子），
     * 但用户真正要回答的是跨作品的问题：「这周还有哪几条没发？」
     * 「上个月发出去的那条链接在哪？」—— 这一页回答这些。
     */
    { path: '/distributions', name: 'distributions', component: () => import('@/views/DistributionsView.vue'), meta: { title: '分发中心' } },
    /**
     * 数据复盘（M8）：发出去之后**效果怎么样**。
     * 和「分发中心」分开两页：分发中心管"发没发、发哪了"，这一页管"发了之后起了多少量"。
     */
    { path: '/metrics', name: 'metrics', component: () => import('@/views/MetricsView.vue'), meta: { title: '数据复盘' } },
    { path: '/assets', name: 'assets', component: () => import('@/views/AssetsView.vue'), meta: { title: '素材与角色' } },
    { path: '/agent', name: 'agent', component: () => import('@/views/AgentView.vue'), meta: { title: '智能体' } },
    { path: '/credits', name: 'credits', component: () => import('@/views/CreditsView.vue'), meta: { title: '积分中心' } },
    { path: '/advisor', name: 'advisor', component: () => import('@/views/PlaceholderView.vue'), meta: { title: '增长陪跑服务' } },
    { path: '/me', name: 'me', component: () => import('@/views/MeView.vue'), meta: { title: '我的' } },
    /**
     * 售后工单
     *
     * 之前遇到问题只能微信找亮哥：没有留痕、用户也看不到进度。
     * 这一页把状态公开（等我们 / 等你 / 已关闭），往来全程留存。
     */
    { path: '/support', name: 'support', component: () => import('@/views/SupportView.vue'), meta: { title: '售后工单' } },
  /** BUG-17：帮助与规范要有**真实页面**，不能是空链接或占位页 */
  { path: '/help', name: 'help', component: () => import('@/views/HelpView.vue'), meta: { title: '帮助与规范' } },

    /**
     * ★ H5 入口（手机分享链接用）
     *
     * H5 与桌面端**是同一套 Vue 代码**，靠 AppShell 的 isMobile(<768px) 切换布局，
     * 所以这里不需要另一份页面 —— 它是一个稳定、好记、可对外发的入口地址：
     *   https://aigc.alitools.cn/h5
     * 未登录会由下面的路由守卫带去 /login（登录页同样是响应式的）。
     */
    /**
     * ★ H5 待办（P2 首条链，2026-10-05）
     *
     * 手机形态的待办页（真实 `GET /todo`）。每条待办引用的任务身份就是
     * `ContentTask.publicId`，点进去是**同一个** `/content-tasks/:id` 详情页。
     * 必须排在 `/h5` 那条重定向**前面**（静态段更长，vue-router 按路径打分也会优先，
     * 但写在前面更直观，也避免以后给 `/h5` 加通配子路由时被误吞）。
     */
    { path: '/h5/todo', name: 'h5-todo', component: () => import('@/views/H5TodoView.vue'), meta: { title: '待办' } },
    { path: '/h5', redirect: '/' },

    /**
     * ★ 平台管理后台登录入口：/admin/login
     *
     * 必须排在 /admin 父路由**之前**（vue-router 按路径打分，静态段优先，
     * 但放前面更直观，也避免以后给父路由加通配子路由时被误吞）。
     * meta.adminEntry 让 LoginView 切换成后台文案 + 登录后校验 platform_* 角色。
     */
    {
      path: '/admin/login',
      name: 'admin-login',
      component: () => import('@/views/LoginView.vue'),
      meta: { title: '平台管理后台登录', public: true, bare: true, adminEntry: true },
    },

    /**
     * 管理后台（平台侧，跨租户）
     *
     * ⚠️ 父路由必须带 `meta.bare: true`：
     *    App.vue 的逻辑是 `route.meta.bare ? <RouterView/> : <AppShell/>`，
     *    不标 bare 会把 AdminLayout 套进工作台的 AppShell，出现两套导航。
     *    子路由会继承父级 meta，所以只在这里写一次。
     *
     * 权限：路由层只校验登录态，真正的准入在服务端（AdminGuard 只放行平台角色）。
     * 前端不按角色隐藏入口 —— 否则会把「本地联调逃生门开着、本来能成功」的操作藏掉。
     */
    {
      path: '/admin',
      component: () => import('@/views/admin/AdminLayout.vue'),
      meta: { title: '平台管理后台', bare: true },
      children: [
        {
          path: '',
          name: 'admin-overview',
          component: () => import('@/views/admin/AdminOverview.vue'),
          meta: { title: '平台总览' },
        },
        {
          path: 'tenants',
          name: 'admin-tenants',
          component: () => import('@/views/admin/AdminTenants.vue'),
          meta: { title: '租户管理' },
        },
        /**
         * ★ 账号 / 订单 / 套餐 三页是 2026-10-04 补的。
         *
         * 之前后台只有"看"的能力（总览/租户/任务/模型用量/合规），
         * 真要运营起来缺三件最日常的事：停用与改角色、对账退款、调套餐。
         * 没有它们，每次都得手工连库 —— 那就等于没有后台。
         */
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/views/admin/AdminUsers.vue'),
          meta: { title: '账号管理' },
        },
        {
          path: 'orders',
          name: 'admin-orders',
          component: () => import('@/views/admin/AdminOrders.vue'),
          meta: { title: '充值订单与对账' },
        },
        {
          path: 'packages',
          name: 'admin-packages',
          component: () => import('@/views/admin/AdminPackages.vue'),
          meta: { title: '充值套餐' },
        },
        {
          path: 'tasks',
          name: 'admin-tasks',
          component: () => import('@/views/admin/AdminTasks.vue'),
          meta: { title: '任务' },
        },
        {
          path: 'model-usage',
          name: 'admin-model-usage',
          component: () => import('@/views/admin/AdminModelUsage.vue'),
          meta: { title: '模型用量' },
        },
        /**
         * 模型通道自检
         *
         * 起因是一次真实故障：通道的 Key 在生产配好了，却被一条闸门静默跳过 ——
         * 接口 200、能力自检报 "none"，只能 SSH 上去翻 .env 和日志。
         * 这一页把「候选表 / 实际装配 / 最近真实调用」并排给出。
         */
        {
          path: 'providers',
          name: 'admin-providers',
          component: () => import('@/views/admin/AdminProviders.vue'),
          meta: { title: '模型通道' },
        },
        {
          path: 'compliance',
          name: 'admin-compliance',
          component: () => import('@/views/admin/AdminCompliance.vue'),
          meta: { title: '合规看板' },
        },
        /**
         * ★ 审计日志页（2026-10-04 补）
         *
         * `GET /admin/audit` 有接口但**一直没有页面**：唯一入口是「账号」页行内的
         * 「变更记录」，而那个入口按某个用户过滤 —— 调分、退款、改套餐、换密钥这些
         * 平台级动作从来没在界面上露过面。这一页把它们摆出来，并支持按
         * targetType + targetId 成对过滤（只比 targetId 会把不同 id 空间的对象混在一起）。
         */
        {
          path: 'audit',
          name: 'admin-audit',
          component: () => import('@/views/admin/AdminAudit.vue'),
          meta: { title: '审计日志' },
        },
        /**
         * ★ 数据源 / 备份状态：以前这两个地址**没有路由** ——
         *   直接访问会落到最外层的客户端 404 外壳（HTTP 200、URL 不变、
         *   写着"页面不存在"，后台导航整块消失）。
         *   路径与接口路径同名（/admin/data-sources、/admin/backup-status），
         *   内容分别来自这两个接口（接口一直都有，只是没有页面）。
         */
        {
          path: 'data-sources',
          name: 'admin-data-sources',
          component: () => import('@/views/admin/AdminDataSources.vue'),
          meta: { title: '对标数据源' },
        },
        {
          path: 'backup-status',
          name: 'admin-backup-status',
          component: () => import('@/views/admin/AdminBackupStatus.vue'),
          meta: { title: '数据备份' },
        },
        {
          path: 'tickets',
          name: 'admin-tickets',
          component: () => import('@/views/admin/AdminTickets.vue'),
          meta: { title: '售后工单' },
        },
        /**
         * ★ 后台区域的通配兜底（必须放在子路由**最后**）
         *
         * 没有它，任何 `/admin/xxx` 未知地址都会落到全局的 `notfound`
         * （`/:pathMatch(.*)*`，meta 里没有 bare）⇒ 页面被 AppShell 再包一层，
         * 或者出现"后台导航整块消失 + 页面不存在"的组合。那会被读成"后台被踢出来了"。
         * 放在父路由 children 里 ⇒ 继承 `bare: true`，仍在 AdminLayout 内渲染。
         */
        {
          path: ':pathMatch(.*)*',
          name: 'admin-notfound',
          component: () => import('@/views/admin/AdminNotFound.vue'),
          meta: { title: '后台页面不存在' },
        },
      ],
    },
    /**
     * ★ 交互稿（屏幕级评审页面）：/review/*
     *
     * ── 为什么单独挂一个 /review 段，而不是塞进现有页面 ──
     * 产品重构第一轮 §9 的边界写得很清楚：三个关键页面**先出交互稿，评审前不铺开**。
     * 所以它们：
     *   ① `meta.bare` —— 不套 AppShell（评审页面要能整屏看，也避免和未来的新导航打架）；
     *      bare 写在父路由上一次，子路由继承（App.vue 只读 `route.meta.bare`）。
     *   ② `meta.public` —— **不接后端接口、也要能在没登录的情况下看**。
     *      不然评审点开会先被守卫甩到 /login，等于看不到稿。
     *   ③ **不进 AppShell 的导航**：交互稿在评审通过前不是功能，放进主导航
     *      （哪怕只是"评审"分组）会让人以为已经上线。入口只在三页之间互相跳 + 直接改地址。
     *   ④ `?state=xxx` —— 七种形态（normal / missing-info / pending-approval /
     *      capability-unavailable / failed / legacy / empty），页面顶部也有切换按钮。
     */
    {
      path: '/review',
      component: () => import('@/views/review/ReviewLayout.vue'),
      meta: { title: '交互稿（评审用）', bare: true, public: true },
      children: [
        { path: '', redirect: '/review/workbench' },
        {
          path: 'workbench',
          name: 'review-workbench',
          component: () => import('@/views/review/ReviewWorkbench.vue'),
          // public/bare 重复写一遍（父级已继承）：评审方可能**没有账号**，
          // 这三行是"未登录也能打开"的**直接判据**，grep 路由文件就能确认，不依赖"继承是不是生效"的记忆。
          meta: { title: '交互稿 · PC 工作台', bare: true, public: true },
        },
        {
          path: 'content-task',
          name: 'review-content-task',
          component: () => import('@/views/review/ReviewContentTask.vue'),
          meta: { title: '交互稿 · 内容任务详情与规划卡', bare: true, public: true },
        },
        {
          path: 'h5-approval',
          name: 'review-h5-approval',
          component: () => import('@/views/review/ReviewH5Approval.vue'),
          meta: { title: '交互稿 · H5 审批与进度', bare: true, public: true },
        },
        /**
         * ★ P2 交互稿：内容任务链（最小首条链）—— 2026-10-05 补
         *
         * 三屏 P1 交互稿只覆盖到「周计划条目」，P2 的首条链（条目 → 内容任务 → 文字脚本
         * → 版本化审批 → 等预算 → 媒体明确暂停）一直没有屏幕稿，评审只能看文档。
         * 这一屏把四形态放进去：**任务列表 / 任务详情 / 版本审批 / H5 待办**。
         *
         * 与上面三屏同规矩：`public: true`（评审方可能没账号）、`bare: true`（不套 AppShell）、
         * **不进 AppShell 主导航**（评审通过前它不是功能）；形态与状态都用 `?screen=` / `?state=` 切，
         * 页面顶部有两组切换按钮。这里重复写 public/bare 是"未登录也能打开"的**直接判据**。
         */
        {
          path: 'p2-chain',
          name: 'review-p2-chain',
          component: () => import('@/views/review/ReviewP2Chain.vue'),
          meta: { title: '交互稿 · P2 内容任务链', bare: true, public: true },
        },
      ],
    },
    { path: '/:pathMatch(.*)*', name: 'notfound', component: () => import('@/views/NotFoundView.vue'), meta: { title: '页面不存在' } },
  ],
  scrollBehavior(_to, _from, saved) {
    return saved ?? { top: 0 };
  },
});

/**
 * 未登录跳登录页（登录页本身放行）
 *
 * ★ 后台走**自己的**登录页 /admin/login，不要甩到商家登录页：
 *   后台账号是平台侧角色，混在商户入口里，运营会找不到北，
 *   商户老板误入也会对着 403 一脸懵。各自入口各自清楚。
 */
router.beforeEach((to) => {
  if (to.meta.public) return true;
  if (!getToken()) {
    const isAdminArea = to.path === '/admin' || to.path.startsWith('/admin/');
    return {
      name: isAdminArea ? 'admin-login' : 'login',
      query: { redirect: to.fullPath },
    };
  }
  return true;
});

export default router;
